import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from "react";

export interface VisiblePanel {
  key: string;
  generation_id?: string | null;
  context?: Record<string, string>;
}
type Report = (instance: symbol, panel?: VisiblePanel) => void;

/** Scoped to main content. Presentations must never register themselves here. */
export const MainPanelPresence = createContext<Report | null>(null);
/** Layout visibility, independent of background-tab/network read suspension. */
export const MainPanelVisibility = createContext(true);

export function useMainPanels() {
  const [panels, setPanels] = useState(new Map<symbol, VisiblePanel>());
  const report = useCallback<Report>((instance, panel) => {
    setPanels(previous => {
      const next = new Map(previous);
      if (panel) next.set(instance, panel);
      else next.delete(instance);
      return next;
    });
  }, []);
  return { panels: [...panels.values()], report };
}

export function useMainPanelPresence(panel: VisiblePanel | undefined) {
  const report = useContext(MainPanelPresence);
  const visible = useContext(MainPanelVisibility);
  const instance = useRef(Symbol("main-panel"));
  const signature = JSON.stringify(panel);
  useLayoutEffect(() => {
    if (!report || !panel || !visible) return;
    report(instance.current, panel);
    return () => report(instance.current);
  }, [report, signature, visible]);
}
